// Consolidated Reports page: Transaction Details, Refund Report and Transaction
// Summary in one tabbed view. Tab content is provided by the existing pages
// rendered with embedded={true}.

import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

// Material Dashboard 2 React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import UnderlineTabs from "components/ui/UnderlineTabs";
import EmptyState from "components/ui/EmptyState";
import usePermission from "components/Utils/usePermission";

// Tab content (existing pages, rendered without their own layout shell)
import CardTransactions from "layouts/reports/CardTransactions";
import RefundReport from "layouts/reports/RefundReport";
import PosTransactions from "layouts/reports/PosTransactions";
import TransactionSummary from "layouts/reports/TransactionSummary";

// i18n
import { useTranslation } from "react-i18next";

function Reports() {
  const checkPermission = usePermission();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabs = useMemo(() => {
    const list = [];
    if (checkPermission("Reports", "CardTransactions")) {
      list.push({ value: "details", label: t("transaction_details") });
      list.push({ value: "refunds", label: t("refund_report") });
    }
    list.push({ value: "pos", label: t("pos_transactions") });
    if (checkPermission("Reports", "TransactionSummary")) {
      list.push({ value: "summary", label: t("transaction_summary") });
    }
    return list;
  }, [checkPermission, t]);

  const requested = searchParams.get("tab");
  const activeTab = tabs.some((tab) => tab.value === requested)
    ? requested
    : tabs.length > 0
    ? tabs[0].value
    : null;

  const handleChange = (event, value) => {
    setSearchParams({ tab: value }, { replace: true });
  };

  // Rendered by whichever tab is active, positioned between its own header
  // row (title/count, date range, export) and its filter+table card.
  const tabsSlot = <UnderlineTabs value={activeTab} onChange={handleChange} tabs={tabs} />;

  return (
    <DashboardLayout>
      <MDBox mt={4}>
        {tabs.length === 0 ? (
          <EmptyState
            icon="lock"
            title={t("no_permission", { defaultValue: "You do not have access to Reports" })}
          />
        ) : (
          <>
            {/* Only the active tab mounts, so each tab's API calls fire on first activation */}
            {activeTab === "details" && <CardTransactions embedded tabsSlot={tabsSlot} />}
            {activeTab === "refunds" && <RefundReport embedded tabsSlot={tabsSlot} />}
            {activeTab === "pos" && <PosTransactions embedded tabsSlot={tabsSlot} />}
            {activeTab === "summary" && <TransactionSummary embedded tabsSlot={tabsSlot} />}
          </>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Reports;
